// C.1 Этап 34 — реализация всех 13 v6 карт Влияния Деуса (юниты со стенда).
// Спящий агент — отдельный файл spyaschiy-agent.js (особая механика).
//
// Подход:
//   - Базовая функция summonInfluenceCard — берёт template из cards-deus.js,
//     создаёт Card, ставит на свою сторону. Cost списывается через chargeInfluence.
//   - Триггеры карт регистрируются глобальными обработчиками (on_died, on_summoned,
//     on_attack, on_turn_start). Проверяем что соответствующий юнит жив у владельца.
//   - Ауры реализованы в startTurn (Двойник Киросы — Иммунитет Герою) и в
//     `effectiveInfluenceCost` (Любовница Танаки — 0 cost).

import { Card } from '../../../card.js';
import { getFactionCards } from '../../../../data/decks.js';
import { applyDamage } from '../../../combat.js';
import { applyHeroDamage } from '../../../hero-damage.js';
import { applyImmuneFirstHit } from '../../../keywords/immune.js';
import { addInfluence, chargeInfluence } from '../influence.js';
import { mindControlUnit } from '../../../effects/mind-control.js';
import { summonUnit } from '../../global/summon.js';
import {
  onUnitSummoned, registerOnTurnStartExtraHandler,
  registerUnitSummonedExtraHandler, registerOnUnitDiedExtraHandler,
  dispatchUnitDied
} from '../hooks.js';
import { registerOnAttackTrigger } from '../../livlife/on-attack-triggers.js';

// ID карт.
const IDS = {
  DVOYNIK: 'deus_inf_dvoynik_kirosy',
  ELITE_STRELOK: 'deus_inf_elite_strelok',
  KLADBISHCHE: 'deus_inf_kladbishche_kontraktov',
  KORPGVARDIYA: 'deus_inf_korpgvardiya',
  KORPSHPION: 'deus_inf_korpshpion',
  KURATOR: 'deus_inf_kurator_operatsiy',
  PALACH: 'deus_inf_lichny_palach',
  SOVETNIK: 'deus_inf_lichny_sovetnik',
  LIKVIDATOR: 'deus_inf_likvidator',
  LYUBOVNITSA: 'deus_inf_lyubovnitsa_tanaki',
  POSREDNIK: 'deus_inf_posrednik',
  TYAZHELY_DRON: 'deus_inf_tyazhely_dron',
  ZNATOK: 'deus_inf_znatok_iskinov'
};

// === Эффективная стоимость с учётом ауры Любовницы Танаки и Мега-сделки ===
// Используется и в canPlayInfluenceUnit, и в обёртке chargeInfluence (см. ниже).
export function effectiveInfluenceCost(state, playerIdx, baseCost) {
  const p = state.players[playerIdx];
  if (!p || baseCost == null) return baseCost;
  // Любовница Танаки — pока жива, призывы из стенда стоят 0.
  if (p.field?.some?.(row => row?.some?.(u => u?.id === IDS.LYUBOVNITSA && (u.current?.hp ?? u.hp ?? 0) > 0))) {
    return 0;
  }
  // Мега-сделка — следующие N призывов с pct-скидкой.
  if ((p.influenceDiscountRemaining || 0) > 0 && p.influenceDiscountPct) {
    return Math.max(0, Math.ceil(baseCost * (1 - p.influenceDiscountPct / 100)));
  }
  return baseCost;
}

// Получить template карты по ID из cards-deus.js.
function getTemplate(cardId) {
  return getFactionCards('deus').find(c => c.id === cardId) || null;
}

// === canPlay (общая) ===
// Проверяет: cost (с учётом скидок), наличие свободного слота, exclusive_hero.
export function canPlayInfluenceUnit(state, playerIdx, cardId) {
  const p = state.players[playerIdx];
  const tpl = getTemplate(cardId);
  if (!tpl) return { ok: false, reason: 'unknown_card' };
  const cost = effectiveInfluenceCost(state, playerIdx, tpl.influence_cost ?? 0);
  if (p.influence < cost) return { ok: false, reason: 'not_enough_influence' };
  if (!p.hasFreeSlot()) return { ok: false, reason: 'no_slot' };
  // Этап 80: гейт exclusive_hero УБРАН — по дизайн-правилу фаундера НЕТ карт,
  // привязанных к конкретному Герою (любой Герой собирает колоду из любых
  // карт). Поле exclusive_hero в данных розыгрыш не ограничивает.
  return { ok: true };
}

// === Призыв на свою сторону ===
// opts.lineHint — желаемая линия (если задана и есть свободная ячейка); иначе random.
export function summonInfluenceCard(state, playerIdx, cardId, opts = {}) {
  const can = canPlayInfluenceUnit(state, playerIdx, cardId);
  if (!can.ok) return can;
  const p = state.players[playerIdx];
  const tpl = getTemplate(cardId);
  const cost = effectiveInfluenceCost(state, playerIdx, tpl.influence_cost ?? 0);
  if (!chargeInfluence(state, playerIdx, cost)) return { ok: false, reason: 'not_enough_influence' };

  // Декремент счётчика Мега-сделки если скидка была применена.
  if ((p.influenceDiscountRemaining || 0) > 0 && p.influenceDiscountPct && cost < (tpl.influence_cost ?? 0)) {
    p.influenceDiscountRemaining -= 1;
    if (p.influenceDiscountRemaining <= 0) p.influenceDiscountPct = 0;
  }

  const card = new Card(tpl);
  card.ownerId = playerIdx;
  card.placedTurn = state.currentTurn;
  card.revealed = true;
  // Найти ячейку.
  const slot = pickFreeSlot(p, opts.lineHint);
  if (!slot) return { ok: false, reason: 'no_slot' };
  p.placeCard(card, slot.line, slot.idx);
  state.log.add(`🪙 ${p.name}: призван "${card.name_ru}" из стенда (${slot.line + 1}-я линия, слот ${slot.idx + 1}, cost=${cost})`);
  // Уведомление хуков.
  try { onUnitSummoned(state, playerIdx, card); } catch (e) { console.warn(e); }
  return { ok: true, card };
}

function pickFreeSlot(p, lineHint = null) {
  if (lineHint != null && p.field?.[lineHint]) {
    const row = p.field[lineHint];
    for (let s = 0; s < row.length; s++) if (row[s] === null) return { line: lineHint, idx: s };
  }
  for (let line = 0; line < 2; line++) {
    const row = p.field?.[line] || [];
    for (let s = 0; s < row.length; s++) if (row[s] === null) return { line, idx: s };
  }
  return null;
}

// === play-wrappers с пост-эффектами (Order) ===

// Базовый — без Order'а (просто призыв).
function playStandard(state, playerIdx, cardId) {
  return summonInfluenceCard(state, playerIdx, cardId);
}

// Ликвидатор — Order: 2 урона выбранному вражескому юниту.
// targetCard ожидается извне (UI/бот выбирает).
function playLikvidator(state, playerIdx, targetCard) {
  const r = summonInfluenceCard(state, playerIdx, IDS.LIKVIDATOR);
  if (!r.ok) return r;
  if (targetCard && targetCard.type === 'unit' && targetCard.ownerId !== playerIdx) {
    const before = { hp: targetCard.hp, armor: targetCard.armor };
    applyDamage(targetCard, 2, { state, pierce: false, source: { id: IDS.LIKVIDATOR } });
    state.log.add(`💥 Ликвидатор → ${targetCard.name_ru}: 2 урона (HP ${before.hp}→${targetCard.hp}, броня ${before.armor}→${targetCard.armor})`);
    if (targetCard.hp <= 0) {
      const opponent = state.players[1 - playerIdx];
      opponent.removeFromField(targetCard);
      state.log.add(`✗ ${targetCard.name_ru} [${opponent.name}] погибает`);
      dispatchUnitDied(state, { ownerIdx: opponent.id, card: targetCard, killer: null, killerIdx: playerIdx, killerWasCounter: false });
    }
  }
  return r;
}

// Посредник — Order: Герой +2 max HP до конца матча.
function playPosrednik(state, playerIdx) {
  const r = summonInfluenceCard(state, playerIdx, IDS.POSREDNIK);
  if (!r.ok) return r;
  const p = state.players[playerIdx];
  if (p.hero) {
    if (p.hero.base) p.hero.base.hp = (p.hero.base.hp ?? 0) + 2;
    if (p.hero.current) p.hero.current.hp = (p.hero.current.hp ?? 0) + 2;
    if (p.hero.hp != null) p.hero.hp += 2;
    state.log.add(`💼 Посредник [${p.name}]: Герой +2 max ХП`);
  }
  return r;
}

// === Регистрация триггеров (глобальная, проверяет наличие на поле) ===

function unitIsAliveOnField(player, cardId) {
  if (!player?.field) return null;
  for (const row of player.field) {
    for (const u of row || []) {
      if (u?.id === cardId && (u.current?.hp ?? u.hp ?? 0) > 0) return u;
    }
  }
  return null;
}

// Кладбище контрактов — on_ally_died → draw 1.
// ДУБЛЬ УБРАН (Этап 63.x): здесь был ВТОРОЙ хендлер с багом — owner.drawOne()
// БЕЗ push в руку (карта терялась), вдобавок к корректному хендлеру в
// deus/c1/units-batch.js (тот кладёт карту в руку). Оставлен только корректный.

// Куратор операций — on_enemy_died → mind_control random remaining enemy atk≤2.
registerOnUnitDiedExtraHandler((state, info) => {
  const { ownerIdx, card } = info;
  // Этот триггер срабатывает у другого игрока (того у кого есть Куратор).
  for (let i = 0; i < state.players.length; i++) {
    if (i === ownerIdx) continue;
    const kuratorOwner = state.players[i];
    const kurator = unitIsAliveOnField(kuratorOwner, IDS.KURATOR);
    if (!kurator) continue;
    if (!kuratorOwner.hasFreeSlot()) continue;
    // Кандидаты — юниты бывшего владельца (это и есть «другой» игрок), atk≤2.
    const enemy = state.players[ownerIdx];
    const candidates = enemy.unitsOnField().filter(u =>
      u.type === 'unit' && u !== card && (u.current?.atk ?? u.atk ?? 0) <= 2
    );
    if (candidates.length === 0) continue;
    const pick = state.rng?.int ? candidates[state.rng.int(candidates.length)] : candidates[0];
    mindControlUnit(state, i, pick);
    state.log.add(`📋 Куратор операций [${kuratorOwner.name}]: угон ${pick.name_ru} (триггер смерти ${card.name_ru})`);
  }
});

// Личный палач — on_attack → +2 урона Герою врага. Регистрируем on_attack-trigger
// напрямую через registerOnAttackTrigger (см. livlife/on-attack-triggers).
registerOnAttackTrigger(IDS.PALACH, (state, attacker, target, damageDealt) => {
  if (!attacker || attacker.ownerId == null) return;
  const ownerIdx = attacker.ownerId;
  const opponentIdx = 1 - ownerIdx;
  applyHeroDamage(state, opponentIdx, 2, { pierce: false });
  state.log.add(`💢 Личный палач [${state.players[ownerIdx].name}]: +2 урона Герою врага (триггер атаки)`);
});

// Личный советник — on_ally_summoned → +1 Влияния владельцу.
// БАГ-фикс (Этап 56.51): дубль удалён. Этот же триггер уже зарегистрирован в
// c1/units-batch.js (общий хендлер для Фин.разведчика И Личного советника).
// Двойная регистрация давала Личному советнику +2 вместо +1 за призыв.

// Знаток искинов — turn_start → призыв Заражённого искином на свободную ячейку.
registerOnTurnStartExtraHandler((state) => {
  for (let i = 0; i < state.players.length; i++) {
    const p = state.players[i];
    const znatok = unitIsAliveOnField(p, IDS.ZNATOK);
    if (!znatok) continue;
    if (!p.hasFreeSlot()) continue;
    const slot = pickFreeSlot(p);
    if (!slot) continue;
    summonUnit(state, i, 'deus_zarazheny_iskinom', slot.line, slot.idx);
    state.log.add(`🤖 Знаток искинов [${p.name}]: призван Заражённый искином (${slot.line + 1}, ${slot.idx + 1})`);
  }
});

// Двойник Киросы — пока жив, Герой владельца получает Иммунитет в начале каждого хода.
registerOnTurnStartExtraHandler((state) => {
  for (let i = 0; i < state.players.length; i++) {
    const p = state.players[i];
    const dvoynik = unitIsAliveOnField(p, IDS.DVOYNIK);
    if (!dvoynik) continue;
    if (!p.hero) continue;
    applyImmuneFirstHit(state, p.hero, IDS.DVOYNIK);
  }
});

// === Диспетчер play (для playInfluenceCard из influence-cards.js) ===

// targeted-карты Влияния, требующие выбора цели игроком.
export const TARGETED_INF_C1 = new Set([IDS.LIKVIDATOR]);

export function needsTargetInfC1(cardId) {
  return TARGETED_INF_C1.has(cardId);
}

// Targets провайдер.
export function getInfluenceTargetsC1(state, playerIdx, cardId) {
  if (cardId !== IDS.LIKVIDATOR) return [];
  const enemy = state.players[1 - playerIdx];
  const r = [];
  for (const row of enemy.field) for (const u of row || []) if (u && u.type === 'unit') r.push(u);
  return r;
}

const INF_RESOLVERS = {
  [IDS.DVOYNIK]:       (s, i, t) => playStandard(s, i, IDS.DVOYNIK),
  [IDS.ELITE_STRELOK]: (s, i, t) => playStandard(s, i, IDS.ELITE_STRELOK),
  [IDS.KLADBISHCHE]:   (s, i, t) => playStandard(s, i, IDS.KLADBISHCHE),
  [IDS.KORPGVARDIYA]:  (s, i, t) => playStandard(s, i, IDS.KORPGVARDIYA),
  [IDS.KORPSHPION]:    (s, i, t) => playStandard(s, i, IDS.KORPSHPION),
  [IDS.KURATOR]:       (s, i, t) => playStandard(s, i, IDS.KURATOR),
  [IDS.PALACH]:        (s, i, t) => playStandard(s, i, IDS.PALACH),
  [IDS.SOVETNIK]:      (s, i, t) => playStandard(s, i, IDS.SOVETNIK),
  [IDS.LIKVIDATOR]:    (s, i, t) => playLikvidator(s, i, t),
  [IDS.LYUBOVNITSA]:   (s, i, t) => playStandard(s, i, IDS.LYUBOVNITSA),
  [IDS.POSREDNIK]:     (s, i, t) => playPosrednik(s, i),
  [IDS.TYAZHELY_DRON]: (s, i, t) => playStandard(s, i, IDS.TYAZHELY_DRON),
  [IDS.ZNATOK]:        (s, i, t) => playStandard(s, i, IDS.ZNATOK)
};

export function isC1InfluenceCard(cardId) {
  return cardId in INF_RESOLVERS;
}

export function canPlayInfluenceC1(state, playerIdx, cardId) {
  if (!isC1InfluenceCard(cardId)) return null; // не наша.
  return canPlayInfluenceUnit(state, playerIdx, cardId);
}

export function playInfluenceC1(state, playerIdx, cardId, targetCard = null) {
  const fn = INF_RESOLVERS[cardId];
  if (!fn) return null;
  return fn(state, playerIdx, targetCard);
}

// Список всех v6 ID для STAND_ORDER (без Спящего, он отдельный).
export const C1_INFLUENCE_IDS = Object.values(IDS);
